import React, { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Chip, LoadingState } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { 
    Car, Motorcycle, SignIn, SignOut, MagnifyingGlass, 
    Clock, CheckCircle, CarProfile 
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function VehicleLogs() {
    const { user } = useAuth();
    const [logs, setLogs] = useState([]);
    const [filteredLogs, setFilteredLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openEntry, setOpenEntry] = useState(false);
    const [openExit, setOpenExit] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [vehicleNumber, setVehicleNumber] = useState("");

    async function load() {
        setLoading(true);
        try {
            const response = await api.get("/vehicle-logs");
            setLogs(response.data);
            setFilteredLogs(response.data);
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
        // Auto-refresh every 30 seconds
        const interval = setInterval(load, 30000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (!searchQuery.trim()) {
            setFilteredLogs(logs);
            return;
        }

        const query = searchQuery.toLowerCase();
        const filtered = logs.filter(log => 
            log.vehicle_number.toLowerCase().includes(query) ||
            (log.vehicle && (
                log.vehicle.owner_name?.toLowerCase().includes(query) ||
                (log.vehicle.flat && `${log.vehicle.flat.block}-${log.vehicle.flat.number}`.toLowerCase().includes(query))
            ))
        );
        setFilteredLogs(filtered);
    }, [searchQuery, logs]);

    async function logEntry(e) {
        e.preventDefault();
        try {
            await api.post("/vehicle-logs", {
                vehicle_number: vehicleNumber.toUpperCase(),
                entry_time: new Date().toISOString()
            });
            toast.success("Vehicle entry logged successfully");
            setOpenEntry(false);
            setVehicleNumber("");
            load();
        } catch (e) {
            toast.error(formatError(e));
        }
    }

    async function logExit(log) {
        try {
            await api.put(`/vehicle-logs/${log.id}`, {
                exit_time: new Date().toISOString()
            });
            toast.success("Vehicle exit logged successfully");
            setOpenExit(null);
            load();
        } catch (e) {
            toast.error(formatError(e));
        }
    }

    const formatTime = (iso) => {
        if (!iso) return "—";
        const date = new Date(iso);
        return date.toLocaleString([], {
            day: "2-digit",
            month: "short",
            hour: "2-digit",
            minute: "2-digit"
        });
    };

    const getDuration = (entry, exit) => {
        if (!entry || !exit) return "—";
        const diff = new Date(exit) - new Date(entry);
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        return `${hours}h ${minutes}m`;
    };

    const activeVehicles = filteredLogs.filter(log => log.entry_time && !log.exit_time);
    const completedLogs = filteredLogs.filter(log => log.exit_time);

    if (loading) {
        return (
            <div className="animate-fade-up">
                <PageHeader overline="Gate" title="Vehicle Logs" description="Society-wide vehicle movement — who's inside, and entry/exit history." />
                <LoadingState />
            </div>
        );
    }

    return (
        <div className="animate-fade-up">
            <PageHeader overline="Gate" title="Vehicle Logs" description="Society-wide vehicle movement — who's inside, and entry/exit history." />

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white border border-brand-line rounded-sm p-4">
                    <p className="text-sm text-brand-inkSoft mb-1">Total Logs Today</p>
                    <p className="text-3xl font-heading font-bold text-brand-ink">{logs.length}</p>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-sm p-4">
                    <p className="text-sm text-green-700 mb-1">Vehicles Inside</p>
                    <p className="text-3xl font-heading font-bold text-green-800">{activeVehicles.length}</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
                    <p className="text-sm text-blue-700 mb-1">Completed</p>
                    <p className="text-3xl font-heading font-bold text-blue-800">{completedLogs.length}</p>
                </div>
                <div className="bg-white border border-brand-line rounded-sm p-4">
                    <Button 
                        onClick={() => setOpenEntry(true)}
                        className="w-full h-full rounded-sm bg-brand-action hover:bg-brand-actionHover text-white"
                    >
                        <SignIn size={24} className="mr-2" />
                        Log Entry
                    </Button>
                </div>
            </div>

            {/* Search */}
            <div className="bg-white border border-brand-line rounded-sm p-4 mb-6">
                <div className="flex items-center gap-3">
                    <MagnifyingGlass size={20} className="text-brand-inkSoft" />
                    <Input
                        placeholder="Search by vehicle number, owner, or flat..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="flex-1"
                    />
                        {searchQuery && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setSearchQuery("")}
                            >
                                Clear
                            </Button>
                        )}
                    </div>
                </div>

                {/* Active Vehicles Section */}
                <div className="mb-6">
                    <h2 className="font-heading text-xl text-brand-ink mb-3 flex items-center gap-2">
                        <Car size={24} className="text-green-600" weight="fill" />
                        Vehicles Inside ({activeVehicles.length})
                    </h2>
                    {activeVehicles.length === 0 ? (
                        <div className="bg-white border border-brand-line rounded-sm p-8 text-center">
                            <p className="text-brand-inkSoft">No vehicles currently inside</p>
                        </div>
                    ) : (
                        <div className="bg-white border border-brand-line rounded-sm overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-green-50">
                                        <tr className="text-left">
                                            <th className="p-3 text-xs font-medium text-brand-ink">Vehicle</th>
                                            <th className="p-3 text-xs font-medium text-brand-ink">Owner / Flat</th>
                                            <th className="p-3 text-xs font-medium text-brand-ink">Entry Time</th>
                                            <th className="p-3 text-xs font-medium text-brand-ink">Duration</th>
                                            <th className="p-3 text-xs font-medium text-brand-ink">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {activeVehicles.map((log, i) => {
                                            const duration = Math.floor((new Date() - new Date(log.entry_time)) / (1000 * 60));
                                            return (
                                                <tr key={log.id} className={i % 2 === 0 ? "bg-white" : "bg-green-50/50"}>
                                                    <td className="p-3">
                                                        <div className="flex items-center gap-2">
                                                            {log.vehicle?.type === "2-wheeler" ? (
                                                                <Motorcycle size={20} className="text-blue-600" />
                                                            ) : (
                                                                <Car size={20} className="text-green-600" />
                                                            )}
                                                            <span className="font-semibold text-brand-ink">
                                                                {log.vehicle_number}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="p-3">
                                                        {log.vehicle ? (
                                                            <div className="text-sm">
                                                                <p className="font-medium text-brand-ink">{log.vehicle.owner_name}</p>
                                                                {log.vehicle.flat && (
                                                                    <p className="text-xs text-brand-inkSoft">
                                                                        {log.vehicle.flat.block}-{log.vehicle.flat.number}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <span className="text-sm text-brand-inkSoft">Unknown</span>
                                                        )}
                                                    </td>
                                                    <td className="p-3 text-sm text-brand-ink">{formatTime(log.entry_time)}</td>
                                                    <td className="p-3">
                                                        <Chip color="yellow" icon={<Clock size={14} />}>
                                                            {duration} min
                                                        </Chip>
                                                    </td>
                                                    <td className="p-3">
                                                        <Button
                                                            size="sm"
                                                            onClick={() => setOpenExit(log)}
                                                            className="rounded-full bg-red-600 hover:bg-red-700 text-white"
                                                        >
                                                            <SignOut size={14} className="mr-1" />
                                                            Log Exit
                                                        </Button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                {/* Completed Logs Section */}
                <div>
                    <h2 className="font-heading text-xl text-brand-ink mb-3 flex items-center gap-2">
                        <CheckCircle size={24} className="text-blue-600" weight="fill" />
                        Completed Logs ({completedLogs.length})
                    </h2>
                    {completedLogs.length === 0 ? (
                        <div className="bg-white border border-brand-line rounded-sm p-8 text-center">
                            <p className="text-brand-inkSoft">No completed logs</p>
                        </div>
                    ) : (
                        <div className="bg-white border border-brand-line rounded-sm overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-100">
                                        <tr className="text-left">
                                            <th className="p-3 text-xs font-medium text-brand-ink">Vehicle</th>
                                            <th className="p-3 text-xs font-medium text-brand-ink">Owner / Flat</th>
                                            <th className="p-3 text-xs font-medium text-brand-ink">Entry</th>
                                            <th className="p-3 text-xs font-medium text-brand-ink">Exit</th>
                                            <th className="p-3 text-xs font-medium text-brand-ink">Duration</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {completedLogs.slice(0, 50).map((log, i) => (
                                            <tr key={log.id} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                                                <td className="p-3">
                                                    <div className="flex items-center gap-2">
                                                        {log.vehicle?.type === "2-wheeler" ? (
                                                            <Motorcycle size={20} className="text-blue-600" />
                                                        ) : (
                                                            <Car size={20} className="text-green-600" />
                                                        )}
                                                        <span className="font-semibold text-brand-ink">
                                                            {log.vehicle_number}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="p-3">
                                                    {log.vehicle ? (
                                                        <div className="text-sm">
                                                            <p className="font-medium text-brand-ink">{log.vehicle.owner_name}</p>
                                                            {log.vehicle.flat && (
                                                                <p className="text-xs text-brand-inkSoft">
                                                                    {log.vehicle.flat.block}-{log.vehicle.flat.number}
                                                                </p>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="text-sm text-brand-inkSoft">Unknown</span>
                                                    )}
                                                </td>
                                                <td className="p-3 text-sm text-brand-ink">{formatTime(log.entry_time)}</td>
                                                <td className="p-3 text-sm text-brand-ink">{formatTime(log.exit_time)}</td>
                                                <td className="p-3 text-sm text-brand-inkSoft">
                                                    {getDuration(log.entry_time, log.exit_time)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {completedLogs.length > 50 && (
                                <div className="p-3 bg-gray-50 border-t border-brand-line text-center text-xs text-brand-inkSoft">
                                    Showing 50 of {completedLogs.length} completed logs
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Log Entry Dialog */}
            <Dialog open={openEntry} onOpenChange={setOpenEntry}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Log Vehicle Entry</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={logEntry} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Vehicle Number *</Label>
                            <Input
                                required
                                placeholder="MH12AB1234"
                                value={vehicleNumber}
                                onChange={(e) => setVehicleNumber(e.target.value.toUpperCase())}
                                className="uppercase text-lg font-semibold"
                                autoFocus
                            />
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
                            <p className="text-sm text-blue-800">
                                <strong>Tip:</strong> You can also scan the vehicle's parking pass QR code for automatic entry logging.
                            </p>
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setOpenEntry(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" className="bg-green-600 hover:bg-green-700 text-white">
                                <SignIn size={16} className="mr-2" />
                                Log Entry
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Log Exit Dialog */}
            <Dialog open={!!openExit} onOpenChange={() => setOpenExit(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Log Vehicle Exit</DialogTitle>
                    </DialogHeader>
                    {openExit && (
                        <div className="space-y-4">
                            <div className="p-4 bg-brand-sage/20 rounded-sm">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-xs text-brand-inkSoft mb-1">Vehicle Number</p>
                                        <p className="font-semibold text-brand-ink text-lg">{openExit.vehicle_number}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-brand-inkSoft mb-1">Entry Time</p>
                                        <p className="font-medium text-brand-ink">{formatTime(openExit.entry_time)}</p>
                                    </div>
                                </div>
                                {openExit.vehicle && (
                                    <div className="mt-3 pt-3 border-t border-brand-line">
                                        <p className="text-sm text-brand-ink">
                                            <strong>Owner:</strong> {openExit.vehicle.owner_name}
                                        </p>
                                        {openExit.vehicle.flat && (
                                            <p className="text-sm text-brand-ink">
                                                <strong>Flat:</strong> {openExit.vehicle.flat.block}-{openExit.vehicle.flat.number}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>

                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setOpenExit(null)}>
                                    Cancel
                                </Button>
                                <Button 
                                    onClick={() => logExit(openExit)}
                                    className="bg-red-600 hover:bg-red-700 text-white"
                                >
                                    <SignOut size={16} className="mr-2" />
                                    Confirm Exit
                                </Button>
                            </DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
